import { jwtDecode } from 'jwt-decode';
import { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './components/navbar/Navbar';
import Unauthorised from './pages/Unauthorised';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import './scss/App.scss';
import { getLoggedInUserProfile, updateUserProfileData } from './services/userService';
import { refreshAccessToken } from "./services/authService";
import { AuthContext } from './services/authContext';
import { ChatContext } from './services/chatContext';
import NotFound from './pages/NotFound';
import routes from './routes';
import * as signalR from "@microsoft/signalr";

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const [baseConnection, setBaseConnection] = useState(null);
    const [chats, setChats] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    const [showNotificationIcon, setShowNotificationIcon] = useState(false);


    function isTokenExpired(token) {
        try {
            const { exp } = jwtDecode(token);
            if (!exp) return true;
            return Date.now() >= exp * 1000;
        } catch {
            return true;
        }
    }

    // Combined authentication check and user profile fetch
    const checkAuthAndFetchUser = async () => {

        setLoading(true);


        let token = null;

        if (localStorage.getItem('accessToken')) {
            token = localStorage.getItem('accessToken');
        } else if (sessionStorage.getItem('accessToken')) {
            token = sessionStorage.getItem('accessToken');
        }

        if (!token) {
            // No token found � log the user out
            //logout();
        }

        if (!!token) {
            if (isTokenExpired(token)) {
                //token is expired, fetch a new one
                console.log("token expired, get another one");
                await fetchNewToken();

                //Re Assign token
                if (localStorage.getItem('accessToken')) {
                    token = localStorage.getItem('accessToken');
                } else if (sessionStorage.getItem('accessToken')) {
                    token = sessionStorage.getItem('accessToken');
                }
            }



            if (!userProfile) {
                try {
                    const userData = await getLoggedInUserProfile();//Errors in this will log the user out
                    setUserProfile(userData);
                    setIsLoggedIn(true);
                } catch (err) {
                    console.error('Error fetching user profile:', err);
                    setUserProfile(null);
                    setIsLoggedIn(false);
                }
            }

        }


        setLoading(false);
    };

    const fetchNewToken = async () => {
        try {
            const data = await refreshAccessToken();
            return data.payload.accessToken;
        } catch (error) {
            console.error('Error refreshing token:', error);
        }
    };

    useEffect(() => {
        checkAuthAndFetchUser();

        if (location.pathname.startsWith("/chat")) {
            setShowNotificationIcon(false);
        }

    }, [location]);



    //Base connection (for listening to chat updates)
    useEffect(() => {
        if (!isLoggedIn) return;
        const startBaseConnection = async () => {
            try {
                const newToken = await fetchNewToken();

                const connection = new signalR.HubConnectionBuilder()
                    .withUrl(process.env.REACT_APP_API_URL + "/updates", {
                        accessTokenFactory: async () => newToken,
                        transport: signalR.HttpTransportType.WebSockets
                    })
                    .withAutomaticReconnect()
                    .build();

                connection.on("ReceiveUpdate", async (receivedChats) => {
                    setChats(receivedChats);
                });

                let originalTitle = document.title;
                let flashInterval = null;

                connection.on("ReceiveMessageUpdate", async (chatId) => {
                    if (!location.pathname.startsWith("/chat")) {
                        setShowNotificationIcon(true);

                        // Flash the title
                        if (!flashInterval) {
                            let showingAlert = false;
                            flashInterval = setInterval(() => {
                                document.title = showingAlert ? originalTitle : "New message!";
                                showingAlert = !showingAlert;
                            }, 1000); // Change title every 1 second
                        }

                        // Stop flashing once user returns to chat
                        const stopFlashing = () => {
                            clearInterval(flashInterval);
                            flashInterval = null;
                            document.title = originalTitle;
                            window.removeEventListener('focus', stopFlashing);
                        };

                        window.addEventListener('focus', stopFlashing);
                    }

                    const audio = new Audio('/notification.wav');
                    await audio.play().catch((err) => console.warn("Audio failed", err));
                });


                await connection.start();

                setBaseConnection(connection);
            } catch (error) {
                console.error("Failed to establish base connection:", error);
            }
        };

        startBaseConnection();

        // Cleanup on unmount
        return () => {
            if (baseConnection) {
                baseConnection.stop().catch(err => console.error("Error stopping base connection:", err));
            }
        };
    }, [isLoggedIn]);


    const logout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');

        setIsLoggedIn(false);
        setUserProfile(null);
        navigate('/');
    };

    const updateUserProfile = async (patchData) => {
        try {
            const updatedUser = await updateUserProfileData(patchData);
            if (updatedUser) {
                setUserProfile(updatedUser);
            }
        } catch (error) {
            console.error("Error updating profile:", error);
        }
    };

    if (loading) {
        return <div className="d-flex text-center vh-100 justify-content-center align-items-center">
            <div className="spinner-grow text-warning" style={{ width: "10rem", height: "10rem" }} role="status">
                <span className="visually-hidden">Loading...</span>
            </div>

        </div>;
    }


    const getUserRoles = () => {
        const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (!token) return [];

        try {
            const decodedToken = jwtDecode(token);
            const roles = decodedToken.role || [];

            if (!roles || (Array.isArray(roles) && roles.length === 0)) {
                logout();
                return [];
            }

            return roles;
        } catch (error) {
            // Invalid token format
            logout();
            return [];
        }
    };

    const userRoles = getUserRoles();

    const hasAccess = (route) => {
        if (!route.roles) return true;
        return route.roles.some(role => userRoles.includes(role));
    };

    return (
        <AuthContext.Provider value={{ isLoggedIn, userProfile, userRoles, logout, updateUserProfile }}>
            <ChatContext.Provider value={{ chats, setChats }}>
                <div id="App">
                    {isLoggedIn && <Navbar showNotificationIcon={showNotificationIcon} setShowNotificationIcon={setShowNotificationIcon} />}
                    <div id="Screen">
                        <Routes>
                            {routes.map(route => {
                                const isAuthorized = hasAccess(route, userProfile?.roles || []);
                                return (
                                    <Route
                                        key={route.path}
                                        path={route.path}
                                        element={isAuthorized ? <route.element /> : <Unauthorised />}
                                    />
                                );
                            })}
                            <Route path="*" element={<NotFound />} />
                        </Routes>

                    </div>
                </div>
            </ChatContext.Provider>
        </AuthContext.Provider>
    );
}

export default App;
